/**
 * The one place a user changes the language the app is written in
 * (docs/TRANSLATION_SPEC.md §13). Each option leads with the language's own
 * name — a parent looking for Spanish looks for "Español", not "Spanish".
 *
 * Signed in, the choice is written to `profile.preferred_language`, so it
 * follows the family to every device and also steers translated push and
 * email. Signed out (the login page), it lives in `localStorage` until there
 * is a profile to write it to.
 *
 * ONE EXCEPTION, and it matters: `preferred_language` is also the *content*
 * translation preference (§4.2), which accepts any BCP-47 tag — far more
 * languages than the chrome ships. When a family has chosen a content
 * language the chrome has no catalog for (Vietnamese, Portuguese…), this
 * switcher changes the chrome only and does not write the profile: a stray
 * click on a two-item menu must not silently cost them translated messages.
 * Account → Language stays the place to change the content language.
 */
import { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import { useTranslation } from 'react-i18next';
import { useUser } from '../contexts/UserContextNew';
import { useFeedback } from '../hooks/useFeedback';
import { UI_LANGUAGES, activeUiLanguage, isUiLanguage, setUiLanguage } from '../i18n';
import { colors } from '../theme/variables';

interface LanguageSwitcherProps {
  /**
   * `icon` is the header form (no label, just the globe and the language);
   * `field` is a labelled select for settings panels and the login page.
   */
  variant?: 'icon' | 'field';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

export function LanguageSwitcher({
  variant = 'icon',
  size = 'small',
  fullWidth = false,
}: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const { user, userProfile, updateUserProfile } = useUser();
  const { notify, feedback } = useFeedback();
  const [pending, setPending] = useState(false);

  const current = activeUiLanguage();
  // Unset, or a language the chrome ships: safe to overwrite. A content-only
  // language (see the note above) is left alone.
  const preference = userProfile?.preferred_language;
  const ownsPreference = !preference || isUiLanguage(preference);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const next = event.target.value;
    setPending(true);
    try {
      const applied = await setUiLanguage(next);
      if (user && ownsPreference) {
        // The profile is the source of truth once there is one to write to.
        await updateUserProfile({ preferred_language: applied });
      }
    } catch {
      notify(t('language.changeFailed'), 'error');
    } finally {
      setPending(false);
    }
  };

  const options = UI_LANGUAGES.map((code) => (
    <MenuItem key={code} value={code}>
      {t(`language.names.${code}`)}
    </MenuItem>
  ));

  if (variant === 'field') {
    return (
      <>
        <FormControl size={size} fullWidth={fullWidth}>
          <InputLabel id="language-switcher-label">{t('language.label')}</InputLabel>
          <Select
            labelId="language-switcher-label"
            label={t('language.label')}
            value={current}
            onChange={handleChange}
            disabled={pending}
            inputProps={{ 'aria-label': t('language.selectLabel') }}
          >
            {options}
          </Select>
        </FormControl>
        {feedback}
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TranslateIcon
          fontSize="small"
          aria-hidden
          sx={{ color: colors.text.secondary, flexShrink: 0 }}
        />
        <Select
          value={current}
          onChange={handleChange}
          disabled={pending}
          variant="standard"
          disableUnderline
          inputProps={{ 'aria-label': t('language.selectLabel') }}
          sx={{
            color: 'text.primary',
            fontSize: '0.875rem',
            '& .MuiSelect-select': { py: 0.25, pr: '20px !important' },
            '& .MuiSelect-icon': { color: colors.text.secondary },
          }}
        >
          {options}
        </Select>
      </Box>
      {feedback}
    </>
  );
}

export default LanguageSwitcher;
